import PropTypes from "prop-types"
import { classNamePropType } from "../../common"

/**
 * PropTypes for AdvancedTable component
 *
 * @property {Array<Object>} columns - Column configuration
 * @property {Array<Object>} data - Table data rows
 * @property {Function} [renderExpanded] - Optional expanded row renderer
 * @property {boolean} [selectable] - Include selection checkboxes
 * @property {string} [title] - Optional table title
 * @property {Function} [onRowClick] - Row click handler
 * @property {boolean} [searchable] - Show search toolbar
 * @property {React.ReactNode} [footerStart] - Content rendered left-aligned in the footer row
 * @property {boolean} [serverSide] - Treat `data` as one already-fetched page; disables internal search/slice
 * @property {number} [totalCount] - Total row count across all pages, required when `serverSide` is true
 * @property {Function} [onPageChange] - Called with the new page index when `serverSide` is true
 * @property {Function} [onRowsPerPageChange] - Called with the new rows-per-page value when `serverSide` is true
 */
export const advancedTablePropTypes = {
	columns: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired,
			align: PropTypes.oneOf(["left", "center", "right"]),
			type: PropTypes.string,
		})
	).isRequired,
	data: PropTypes.arrayOf(PropTypes.object).isRequired,
	className: classNamePropType,
	renderExpanded: PropTypes.func,
	selectable: PropTypes.bool,
	title: PropTypes.string,
	onRowClick: PropTypes.func,
	searchable: PropTypes.bool,
	footerStart: PropTypes.node,
	serverSide: PropTypes.bool,
	totalCount: PropTypes.number,
	onPageChange: PropTypes.func,
	onRowsPerPageChange: PropTypes.func,
}

/**
 * PropTypes for TableRow component
 *
 * @property {Object} row - Row data object
 * @property {Array<{id: string, label?: string, align?: 'left'|'center'|'right', type?: string}>} columns - Column definitions
 * @property {Function} [renderExpanded] - Optional renderer for expanded row content
 * @property {boolean} [isSelected] - Selection state for checkbox rows
 * @property {Function} [onClick] - Checkbox click handler
 * @property {Function} [onRowClick] - Row click handler
 * @property {boolean} [selectable] - Whether to show selection checkbox
 * @property {string} [colorText] - Text color
 * @property {string} [colorCard] - Card background color
 * @property {string} [className] - Additional CSS classes
 */
export const tableRowPropTypes = {
	row: PropTypes.object.isRequired,
	columns: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string.isRequired,
			label: PropTypes.string,
			align: PropTypes.oneOf(["left", "center", "right"]),
			type: PropTypes.string,
		})
	).isRequired,
	renderExpanded: PropTypes.func,
	isSelected: PropTypes.bool,
	onClick: PropTypes.func,
	onRowClick: PropTypes.func,
	selectable: PropTypes.bool,
	colorText: PropTypes.string,
	colorCard: PropTypes.string,
	className: classNamePropType,
}
